import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getHrCycleActivity } from "../../../api/api";

// ======================================================
// DATE FORMATTERS (DD-MM-YYYY)
// ======================================================

const formatDate = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDateTime = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${day}-${month}-${year}, ${time}`;
};

export default function CycleActivityDialog({ open, onClose, user, initialTab }) {

  const [tab, setTab] = useState(initialTab || "applied");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && user) {
      setTab(initialTab || "applied");
      load();
    }
  }, [open, user]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getHrCycleActivity(user.user_id);
      setData(res);
    } catch (err) {
      console.error("Failed to load cycle activity:", err);
    } finally {
      setLoading(false);
    }
  };

  const rows = data ? data[tab] || [] : [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {user?.name}
          </Typography>
          {data && (
            <Typography variant="body2" color="text.secondary">
              Cycle: {formatDate(data.cycle_start)} → {formatDate(data.cycle_end)}
            </Typography>
          )}
        </Box>

        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Applied" value="applied" />
        <Tab label="Approved" value="approved" />
        <Tab label="LOP" value="lop" />
      </Tabs>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, background: "#f5f7fa" } }}>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Dates</TableCell>
                  {tab === "applied" && (
                    <>
                      <TableCell>Applied At</TableCell>
                      <TableCell>Status</TableCell>
                    </>
                  )}
                  {tab === "approved" && (
                    <>
                      <TableCell>Approved At</TableCell>
                      <TableCell>Approved By</TableCell>
                    </>
                  )}
                  {tab === "lop" && <TableCell align="right">LOP Days</TableCell>}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.leave_type_name}</TableCell>
                    <TableCell>
                      {formatDate(row.from_date)} → {formatDate(row.to_date)}
                    </TableCell>
                    {tab === "applied" && (
                      <>
                        <TableCell>{formatDateTime(row.applied_at)}</TableCell>
                        <TableCell>{row.status}</TableCell>
                      </>
                    )}
                    {tab === "approved" && (
                      <>
                        <TableCell>{formatDateTime(row.approved_at)}</TableCell>
                        <TableCell>{row.approved_by_name || "—"}</TableCell>
                      </>
                    )}
                    {tab === "lop" && <TableCell align="right">{row.lop_days}</TableCell>}
                  </TableRow>
                ))}

                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}